import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import SydModal from '../../../../commonDesign/SydModal'
import SaveCancelButtons from '../../../../molecules/SaveCancelButtons'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydDatePicker from '../../../../commonDesign/SydDatePicker'
import { useCreateGoals, useModifyGoals } from '../../../../../api/goals'
import SydTextArea from '../../../../commonDesign/SydTextArea'

export const modalStyles = makeStyles(() => ({
  root: {
    height: '25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  selectField: {
    width: '100%',
    minWidth: '300px',
    minHeight: '52px'
  },
  buttonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  },
  fullRow: {
    flex: 1
  },
  bottomSpace: {
    marginBottom: '1rem'
  }
}))
const GoalModal = ({ open, onClose, goalSchema }) => {
  const classes = modalStyles()
  const isEdit = goalSchema?.goalId != null
  const { mutateAsync: addGoal, isLoading } = useCreateGoals()
  const { mutateAsync: modifyGoal, isLoading: modifyLoading } = useModifyGoals()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      code: null,
      name: null,
      description: null,
      value: null,
      unit: null,
      startDate: null,
      endDate: null
    }
  })
  useEffect(() => {
    if (!open || !goalSchema) return
    form.reset({
      code: goalSchema.code ?? null,
      name: goalSchema.name ?? null,
      description: goalSchema.description ?? null,
      value: goalSchema.value ?? null,
      unit: goalSchema.unit ?? null,
      startDate: goalSchema.startDate ?? null,
      endDate: goalSchema.endDate ?? null
    })
  }, [open, goalSchema, form])
  const [submitError, setSubmitError] = useState(null)
  const onSubmit = useCallback(async (data) => {
    setSubmitError(null)
    try {
      let result

      if (isEdit) {
        result = await modifyGoal({
          goalId: goalSchema.goalId,
          name: data.name,
          description: data.description,
          value: data.value,
          unit: data.unit,
          startDate: data.startDate,
          endDate: data.endDate,
          levelTypeId: goalSchema?.levelTypeId
        })
      } else {
        result = await addGoal({ ...data, levelTypeId: goalSchema?.levelTypeId })
      }
      if (result && result.success) {
        form.reset()
        onClose()
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to add goal')
    }
  }, [onClose, form, addGoal, modifyGoal, isEdit, goalSchema?.levelTypeId, goalSchema?.goalId])
  const handleClose = useCallback(() => {
    form.reset({
      code: null,
      name: null,
      description: null,
      value: null,
      unit: null,
      startDate: null,
      endDate: null
    })
    setSubmitError(null)
    onClose()
  }, [form, onClose])

  return (
    <SydModal open={open} onClose={handleClose} title={isEdit ? 'Edit Goal' : 'Add Goal'}>
      <div className={classes.inputRow}>
        <Controller
          name='code'
          control={form.control}
          rules={{
            required: { value: true, message: 'Code is required' },
            maxLength: { value: 64, message: 'Code cannot exceed 64 characters' },
            pattern: {
              value: /^[a-zA-Z][a-zA-Z0-9]*$/,
              message: 'Code must start with a letter and contain only letters and numbers (e.g. myGoal)'
            }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Code'
              required
              description='Must start with a letter and contain only letters and numbers'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput {...field} error={fieldState.error} disabled={Boolean(goalSchema?.goalId)} />
            </SydLabel>
          )}
        />
        <Controller
          name='name'
          control={form.control}
          rules={{
            required: { value: true, message: 'Name is required' },
            maxLength: { value: 128, message: 'Name cannot exceed 128 characters' }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Name'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput {...field} error={fieldState.error} className={classes.bottomSpace} />
            </SydLabel>
          )}
        />
      </div>
      <div>
        <Controller
          name='description'
          control={form.control}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Description'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydTextArea
                {...field}
                value={field.value ?? ''}
                inputProps={{ rows: 3 }}
                error={fieldState.error}
              />
            </SydLabel>
          )}
        />
      </div>
      <div className={classes.inputRow}>
        <Controller
          name='value'
          control={form.control}
          rules={{
            validate: (value) => {
              if (value === '' || value === undefined || value === null) {
                return true
              }
              const s = String(value).trim()
              if (s === '') return true
              const n = Number(s)
              if (!Number.isFinite(n)) {
                return 'Must be a valid number'
              }
              const parts = s.split('.')
              if (parts.length > 1 && parts[1].length > 6) {
                return 'Use at most 6 decimal places'
              }
              return true
            }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Value'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput {...field} error={fieldState.error} />
            </SydLabel>
          )}
        />
        <Controller
          name='unit'
          control={form.control}
          rules={{
            maxLength: { value: 16, message: 'Unit cannot exceed 16 characters' }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Unit'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput {...field} error={fieldState.error} />
            </SydLabel>
          )}
        />
      </div>
      <div className={classes.inputRow}>
        <Controller
          name='startDate'
          control={form.control}
          rules={{
            required: { value: true, message: 'Start date is required' },
            validate: () => {
              form.trigger('endDate')
              return true
            }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Start Date'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydDatePicker
                {...field}
                type='date'
                dateFormat='YYYY-MM-DD'
                size='sm'
              />
            </SydLabel>
          )}
        />
        <Controller
          name='endDate'
          control={form.control}
          rules={{
            required: { value: true, message: 'End date is required' },
            validate: (value) => {
              const startDate = form.getValues('startDate')
              if (!value || !startDate) return true
              return new Date(value) >= new Date(startDate) || 'End date must be on or after start date'
            }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='End Date'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydDatePicker
                {...field}
                type='date'
                dateFormat='YYYY-MM-DD'
                size='sm'
              />
            </SydLabel>
          )}
        />
      </div>

      <div className={classes.buttonRow}>
        {submitError && <div style={{ color: 'red', marginBottom: '1rem' }}>{submitError}</div>}
        <SaveCancelButtons
          containerClassName={classes.saveCancel}
          onSave={form.handleSubmit(onSubmit)}
          onCancel={handleClose}
          isSubmitting={isLoading || modifyLoading}
        />
      </div>
    </SydModal>
  )
}

GoalModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  goalSchema: PropTypes.object
}

export default GoalModal
